import { useMemo, useState } from 'react';
import { Modal, Spinner, Badge } from '@centraoneplus/ui';
import { FileText, Clock, Calendar, Inbox } from 'lucide-react';
import { ExportButton } from '@components/common/ExportButton';
import type { ExportColumn } from '@utils/export';
import { useGeneratedReports, useGeneratedReport } from '../../hooks/useAnalytics';

// ── Formatting helpers (mirror the analytics service's report renderer) ──────
function fmtHours(value: unknown): string {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? `${n.toFixed(1)} h` : '—';
}
function num(value: unknown): string {
  const n = typeof value === 'number' ? value : parseInt(String(value), 10);
  return Number.isFinite(n) ? String(n) : '0';
}
function fmtCsat(value: unknown): string {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) && n > 0 ? n.toFixed(1) : '—';
}

function formatDateTime(dateStr?: string | null): string {
  if (!dateStr) return '--';
  return new Date(dateStr).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface MetricRow {
  label: string;
  value: string;
}
interface AgentRow {
  name: string;
  handled: string;
  resolved: string;
  avgResponse: string;
  avgResolution: string;
  csat: string;
}

type Metrics = Record<string, any>;

// Build the (label, value) rows shown for a report type — mirrors the
// analytics service `_rows_for`, so the UI matches the emailed report.
function rowsFor(reportType: string, m: Metrics): MetricRow[] {
  if (reportType === 'sla_compliance') {
    return [
      { label: 'SLA compliance rate', value: `${m.sla_compliance_rate ?? 0}%` },
      { label: 'First response — met', value: num(m.first_response_sla_met) },
      { label: 'First response — breached', value: num(m.first_response_sla_breached) },
      { label: 'Resolution — met', value: num(m.resolution_sla_met) },
      { label: 'Resolution — breached', value: num(m.resolution_sla_breached) },
    ];
  }

  if (reportType === 'ticket_volume') {
    const rows: MetricRow[] = [
      { label: 'Total tickets', value: num(m.total_tickets) },
      { label: 'New', value: num(m.new_tickets) },
      { label: 'In progress', value: num(m.in_progress_tickets) },
      { label: 'Resolved', value: num(m.resolved_tickets) },
      { label: 'Email', value: num(m.email_tickets) },
      { label: 'Chat', value: num(m.chat_tickets) },
      { label: 'WhatsApp', value: num(m.whatsapp_tickets) },
      { label: 'Voice', value: num(m.voice_tickets) },
    ];
    const trend: Array<{ month?: string; count?: number }> = m.year_trend || [];
    if (trend.length) {
      rows.push({ label: `— ${m.year ?? ''} monthly volume —`, value: '' });
      for (const t of trend) rows.push({ label: String(t.month), value: num(t.count) });
    }
    return rows;
  }

  // daily_summary / weekly_summary (and any fallback)
  return [
    { label: 'Total tickets', value: num(m.total_tickets) },
    { label: 'New', value: num(m.new_tickets) },
    { label: 'In progress', value: num(m.in_progress_tickets) },
    { label: 'Resolved', value: num(m.resolved_tickets) },
    { label: 'Urgent', value: num(m.urgent_tickets) },
    { label: 'High priority', value: num(m.high_priority_tickets) },
    { label: 'Avg first response', value: fmtHours(m.avg_response_time) },
    { label: 'Avg resolution', value: fmtHours(m.avg_resolution_time) },
  ];
}

const METRIC_COLUMNS: ExportColumn<MetricRow>[] = [
  { key: 'label', header: 'Metric' },
  { key: 'value', header: 'Value' },
];
const AGENT_COLUMNS: ExportColumn<AgentRow>[] = [
  { key: 'name', header: 'Agent' },
  { key: 'handled', header: 'Handled' },
  { key: 'resolved', header: 'Resolved' },
  { key: 'avgResponse', header: 'Avg Response' },
  { key: 'avgResolution', header: 'Avg Resolution' },
  { key: 'csat', header: 'CSAT' },
];

interface GeneratedReportsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function GeneratedReportsModal({ isOpen, onClose }: GeneratedReportsModalProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data: list, isLoading: listLoading } = useGeneratedReports(undefined, isOpen);

  // Default the selection to the newest snapshot once the list loads, and drive
  // the detail fetch off that same id (not the raw click state) so the
  // auto-selected report actually loads on open.
  const effectiveId = selectedId ?? list?.[0]?.id ?? null;
  const { data: detail, isLoading: detailLoading, isError: detailError } =
    useGeneratedReport(effectiveId);
  const activeDetail = detail && detail.id === effectiveId ? detail : null;

  const isAgentReport = activeDetail?.report_type === 'agent_performance';

  const agentRows: AgentRow[] = useMemo(() => {
    if (!isAgentReport || !activeDetail) return [];
    const agents: any[] = activeDetail.metrics?.agents || [];
    return agents.map((a) => ({
      name: a.name ?? '—',
      handled: num(a.handled),
      resolved: num(a.resolved),
      avgResponse: fmtHours(a.avg_response),
      avgResolution: fmtHours(a.avg_resolution),
      csat: fmtCsat(a.csat),
    }));
  }, [isAgentReport, activeDetail]);

  const metricRows: MetricRow[] = useMemo(() => {
    if (!activeDetail || isAgentReport) return [];
    return rowsFor(activeDetail.report_type, activeDetail.metrics || {});
  }, [activeDetail, isAgentReport]);

  const exportFilename = activeDetail
    ? `${activeDetail.report_type}_${(activeDetail.generated_at || '').slice(0, 10)}`
    : 'generated_report';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Scheduler-Generated Reports" size="xl">
      <div className="flex flex-col md:flex-row gap-4 min-h-[420px]">
        {/* Snapshot list */}
        <div className="md:w-64 shrink-0 border-r border-gray-200 dark:border-gray-700 md:pr-3">
          {listLoading ? (
            <div className="flex items-center justify-center py-10">
              <Spinner size="md" />
            </div>
          ) : !list || list.length === 0 ? (
            <div className="text-center py-10 text-sm text-gray-500 dark:text-gray-400">
              <Inbox className="h-8 w-8 mx-auto mb-2 text-gray-300 dark:text-gray-600" />
              No generated reports yet.
              <p className="mt-1 text-xs">
                Enable “Show on Reports page” on a Report Generation job.
              </p>
            </div>
          ) : (
            <ul className="space-y-1 max-h-[420px] overflow-y-auto">
              {list.map((r) => {
                const active = r.id === effectiveId;
                return (
                  <li key={r.id}>
                    <button
                      onClick={() => setSelectedId(r.id)}
                      className={`w-full text-left rounded-md px-3 py-2 transition-colors ${
                        active
                          ? 'bg-primary-50 dark:bg-primary-900/30'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-gray-400 shrink-0 dark:text-gray-500" />
                        <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {r.title}
                        </span>
                      </div>
                      <div className="mt-0.5 flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                        <Calendar className="h-3 w-3" />
                        {formatDateTime(r.generated_at)}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Selected report */}
        <div className="flex-1 min-w-0">
          {!effectiveId ? (
            <div className="flex items-center justify-center h-full text-sm text-gray-400 dark:text-gray-500">
              Select a report to view it.
            </div>
          ) : detailLoading ? (
            <div className="flex items-center justify-center py-16">
              <Spinner size="lg" />
            </div>
          ) : detailError || !activeDetail ? (
            <div className="flex items-center justify-center py-16 text-sm text-gray-500 dark:text-gray-400">
              Couldn’t load this report. Please try again.
            </div>
          ) : (
            <div>
              {/* Header + export */}
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {activeDetail.title}
                  </h3>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                    {activeDetail.window_days != null && (
                      <span className="inline-flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Last {activeDetail.window_days} day
                        {activeDetail.window_days === 1 ? '' : 's'}
                      </span>
                    )}
                    <span>· Generated {formatDateTime(activeDetail.generated_at)}</span>
                    {activeDetail.source_job_name && (
                      <Badge variant="secondary">Job: {activeDetail.source_job_name}</Badge>
                    )}
                  </div>
                </div>
                {isAgentReport ? (
                  <ExportButton
                    data={agentRows}
                    columns={AGENT_COLUMNS}
                    filename={exportFilename}
                    sheetName={activeDetail.title}
                    primaryTitle={activeDetail.title}
                    variant="outline"
                    size="sm"
                  />
                ) : (
                  <ExportButton
                    data={metricRows}
                    columns={METRIC_COLUMNS}
                    filename={exportFilename}
                    sheetName={activeDetail.title}
                    primaryTitle={activeDetail.title}
                    variant="outline"
                    size="sm"
                  />
                )}
              </div>

              {/* Body */}
              {isAgentReport ? (
                agentRows.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 py-6">
                    No agent activity in this period.
                  </p>
                ) : (
                  <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                          <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">Agent</th>
                          <th className="px-3 py-2 text-right font-medium text-gray-500 dark:text-gray-400">Handled</th>
                          <th className="px-3 py-2 text-right font-medium text-gray-500 dark:text-gray-400">Resolved</th>
                          <th className="px-3 py-2 text-right font-medium text-gray-500 dark:text-gray-400">Avg Resp</th>
                          <th className="px-3 py-2 text-right font-medium text-gray-500 dark:text-gray-400">Avg Res</th>
                          <th className="px-3 py-2 text-right font-medium text-gray-500 dark:text-gray-400">CSAT</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {agentRows.map((a, i) => (
                          <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                            <td className="px-3 py-2 text-gray-900 dark:text-white">{a.name}</td>
                            <td className="px-3 py-2 text-right text-gray-600 dark:text-gray-300">{a.handled}</td>
                            <td className="px-3 py-2 text-right text-gray-600 dark:text-gray-300">{a.resolved}</td>
                            <td className="px-3 py-2 text-right text-gray-600 dark:text-gray-300">{a.avgResponse}</td>
                            <td className="px-3 py-2 text-right text-gray-600 dark:text-gray-300">{a.avgResolution}</td>
                            <td className="px-3 py-2 text-right text-gray-600 dark:text-gray-300">{a.csat}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              ) : (
                <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
                  <table className="w-full text-sm">
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {metricRows.map((row, i) => (
                        <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="px-4 py-2 text-gray-600 dark:text-gray-300">{row.label}</td>
                          <td className="px-4 py-2 text-right font-semibold text-gray-900 dark:text-white">
                            {row.value}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {activeDetail.metrics?.avg_approximate && (
                <p className="mt-3 text-xs text-amber-600 dark:text-amber-400">
                  Note: counts are exact; average times are based on a sample of the most recent
                  200 tickets in this period.
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
